import { FC } from 'react';
import { Avatar, Box, HStack, Stack, Text, Tooltip } from '@chakra-ui/react';

import { isEmpty, isNil } from 'ramda';
import { EventFragment, EventType } from '../../graphql/generated';
import {
  eventTypeMap,
  getEventDetails,
} from '../file-activity-panel/format-events';

type EventProps = {
  event: EventFragment;
  cedantName: string;
  onClickFile: (event: EventFragment) => () => void;
  onClickFolder: (event: EventFragment) => () => void;
  onClickParentFolder: (event: EventFragment) => () => void;
};

const isServiceAccount = ({ user }: EventFragment) => {
  if (!isEmpty(user.email) && !isNil(user.email)) {
    return user.email.split('@').includes('gserviceaccount.com');
  }
  return false;
};

const getEventAuthor = (event: EventFragment) => {
  const eventAuthor = isServiceAccount(event) ? 'Automation' : event.user.name;

  return eventAuthor;
};

export const Event: FC<EventProps> = ({
  event,
  cedantName,
  onClickFile,
  onClickFolder,
  onClickParentFolder,
}) => {
  const { folder, filename, path } = getEventDetails(cedantName, event.path);
  const eventAuthor = getEventAuthor(event);
  const eventTitle = `${eventAuthor} ${eventTypeMap[event.type]}`;
  const isFolderCreated = event.type === EventType.FOLDER_CREATED;
  const isFolderDownloaded = event.type === EventType.FOLDER_DOWNLOADED;

  const pathPrefix = path ? `/${path}` : '';
  const folderDownloadTooltip = `${cedantName}${pathPrefix}/${filename}`;
  const parentFolderTooltip = `${cedantName}${pathPrefix}`;

  return (
    <HStack spacing="4" data-testid="activity-feed-list-item">
      <Avatar name={eventAuthor || ''} />
      <Box maxW="90%">
        <Text
          isTruncated
          fontWeight="600"
          title={`${eventTitle} ${isFolderDownloaded ? filename : folder}`}
          data-testid="event-details"
        >
          {eventTitle}{' '}
          {isFolderDownloaded ? (
            <Tooltip
              placement="top"
              label={folderDownloadTooltip}
            >
              <Text
                as="span"
                isTruncated
                cursor="pointer"
                fontWeight="bold"
                data-testid="folder"
                textDecoration="underline"
                onClick={onClickFolder(event)}
              >
                {filename}
              </Text>
            </Tooltip>
          ) : (
            <Tooltip
              placement="top"
              label={parentFolderTooltip}
            >
              <Text
                as="span"
                isTruncated
                cursor="pointer"
                fontWeight="bold"
                data-testid="folder"
                textDecoration="underline"
                onClick={onClickParentFolder(event)}
              >
                {folder}
              </Text>
            </Tooltip>
          )}
        </Text>
        {!isFolderDownloaded && (
          <Stack>
            <Text
              as="span"
              isTruncated
              cursor="pointer"
              title={filename}
              data-testid="filename"
              onClick={
                isFolderCreated ? onClickFolder(event) : onClickFile(event)
              }
            >
              {filename}
            </Text>
          </Stack>
        )}
      </Box>
    </HStack>
  );
};
